import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/useAuthStore';
import { Plus, Car, Wrench, AlertCircle, Loader2 } from 'lucide-react';
import type { Database } from '../lib/database.types';
import { AddCarModal } from '../components/AddCarModal';
import { AddServiceRequestModal } from '../components/AddServiceRequestModal';
import { useTranslation } from '../hooks/useTranslation';

type Car = Database['public']['Tables']['cars']['Row'];
type ServiceRequest = Database['public']['Tables']['service_requests']['Row'] & {
  car: Car | null;
  images: Array<{ image_url: string }>;
};

export function DashboardPage() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [cars, setCars] = useState<Car[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddCarModalOpen, setIsAddCarModalOpen] = useState(false);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);

  const fetchData = async () => {
    if (!user) return;

    try {
      setError(null);
      // Fetch cars
      const { data: carsData, error: carsError } = await supabase
        .from('cars')
        .select('*')
        .eq('profile_id', user.id)
        .order('created_at', { ascending: false });

      if (carsError) throw carsError;
      setCars(carsData);

      // Fetch service requests with car details and images
      const { data: requestsData, error: requestsError } = await supabase
        .from('service_requests')
        .select(`
          *,
          car:cars(*),
          images:service_request_images(image_url)
        `)
        .eq('profile_id', user.id)
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;
      setServiceRequests(requestsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const handleCarAdded = () => {
    fetchData();
  };

  const handleServiceAdded = () => {
    fetchData();
  };

  const approvedCars = cars.filter(car => car.status === 'approved');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <div className="text-lg text-gray-600 dark:text-gray-300">{t.common.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Dashboard Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t.dashboard.title}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {t.dashboard.subtitle}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Cars Section */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Car className="w-5 h-5 mr-2" />
              {t.dashboard.vehicles.title}
            </h2>
            <button
              onClick={() => setIsAddCarModalOpen(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t.dashboard.vehicles.addVehicle}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cars.map((car) => (
              <div
                key={car.id}
                className="border dark:border-gray-600 rounded-lg p-4"
              >
                {car.status === 'pending' ? (
                  <div className="text-center py-4">
                    <span className="text-lg font-medium text-yellow-600 dark:text-yellow-400">
                      Pending Review
                    </span>
                  </div>
                ) : (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {car.brand} {car.model}
                    </h3>
                    <div className="space-y-2 text-gray-600 dark:text-gray-300">
                      <p>{t.dashboard.vehicles.year}: {car.year}</p>
                      {car.chassis_number && (
                        <p>Chassis: {car.chassis_number}</p>
                      )}
                      {car.plate_state && car.plate_code && car.plate_number && (
                        <p>Plate: {car.plate_state} {car.plate_code} {car.plate_number}</p>
                      )}
                      <p>
                        {t.dashboard.vehicles.status}: <span className="capitalize">{car.status}</span>
                      </p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Service Requests Section */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Wrench className="w-5 h-5 mr-2" />
              {t.dashboard.serviceRequests.title}
            </h2>
            <button
              onClick={() => setIsAddServiceModalOpen(true)}
              disabled={approvedCars.length === 0}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              title={approvedCars.length === 0 ? "You need an approved vehicle to request service" : undefined}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t.dashboard.serviceRequests.newRequest}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b dark:border-gray-600">
                  <th className="pb-3 text-gray-900 dark:text-white">{t.dashboard.serviceRequests.date}</th>
                  <th className="pb-3 text-gray-900 dark:text-white">Vehicle</th>
                  <th className="pb-3 text-gray-900 dark:text-white">{t.dashboard.serviceRequests.description}</th>
                  <th className="pb-3 text-gray-900 dark:text-white">{t.dashboard.serviceRequests.status}</th>
                  <th className="pb-3 text-gray-900 dark:text-white">{t.dashboard.serviceRequests.quote}</th>
                </tr>
              </thead>
              <tbody>
                {serviceRequests.map((request) => (
                  <tr key={request.id} className="border-b dark:border-gray-600">
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      {new Date(request.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      {request.car ? `${request.car.brand} ${request.car.model}` : 'Unknown'}
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      <div>
                        {request.description}
                        {request.images && request.images.length > 0 && (
                          <div className="mt-2 flex gap-2">
                            {request.images.map((image, index) => (
                              <a
                                key={index}
                                href={image.image_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                View Image {index + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3">
                      <span className="capitalize px-2 py-1 rounded text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {request.status}
                      </span>
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-300">
                      {request.quote_amount
                        ? `AED ${request.quote_amount}`
                        : t.dashboard.serviceRequests.pending}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddCarModal
        isOpen={isAddCarModalOpen}
        onClose={() => setIsAddCarModalOpen(false)}
        onCarAdded={handleCarAdded}
      />

      <AddServiceRequestModal
        isOpen={isAddServiceModalOpen}
        onClose={() => setIsAddServiceModalOpen(false)}
        onRequestAdded={handleServiceAdded}
        approvedCars={approvedCars}
      />
    </div>
  );
}